import { useCallback, useEffect, useState } from "react";
import { useUrlState } from "@/lib/useUrlState";
import { Loader2, ShoppingCart, Layers, Share, Mail, Download, ChevronRight, ChevronDown,
  ChevronsDownUp, ChevronsUpDown, ArrowLeft, Minimize2, Maximize2, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import ReadinessBoard from "./ReadinessBoard";
import BoardQuoteDialog from "./BoardQuoteDialog";
import PoDetail from "./PoDetail";
import InboxSection from "./InboxSection";
import Hint from "@/components/Hint";

const STATUS_WORD = {
  estimate: "in Estimate", ready: "Ready", on_hold: "On Hold", completed: "Completed",
  closed: "Closed", cancelled: "Cancelled",
};
const SECTIONS = [["board", "Queue"], ["pos", "Purchase orders"], ["receiving", "Receiving"], ["inbox", "Inbox"]];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2,
  maximumFractionDigits: 2 })}`;
const n = (v) => Number(v || 0).toLocaleString();

/** OPS-5 — one tab: what is ready to buy, the purchase orders themselves, what has landed, and
 *  the purchasing@ inbox. Every PO is prepared from the FRS, the approved submittal and the COs. */
export default function PurchasingTab({ project, reload }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useUrlState("po", "");  // open PO is a history entry
  const [section, setSection] = useState(openId ? "pos" : "board");
  const [allOpen, setAllOpen] = useState(false);
  const [focus, setFocus] = useState(() => localStorage.getItem("ops.po.focus") !== "0");
  const toggleFocus = () => setFocus((f) => { localStorage.setItem("ops.po.focus", f ? "0" : "1"); return !f; });
  const [receipts, setReceipts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quoteFor, setQuoteFor] = useState(false);   // false closed · null all · group scoped
  const emailLog = async () => {
    try {
      const d = await opsApi.emailDraft(project.id, "po_log");
      opsApi.poLogPdf(project.id).catch(() => toast.error("The PDF log could not be downloaded"));
      const to = (d.to || []).join(",");
      const cc = (d.cc || []).length ? `&cc=${encodeURIComponent(d.cc.join(","))}` : "";
      window.location.href = `mailto:${to}?subject=${encodeURIComponent(d.subject || "")}${cc}&body=${encodeURIComponent(d.body || "")}`;
      toast.success("Log PDF downloaded — attach it to the email that just opened");
    } catch { toast.error("Could not prepare that email"); }
  };

  const load = useCallback(async () => {
    try { setData(await opsApi.purchasing(project.id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load purchasing"); }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (section === "receiving") {
      opsApi.poReceipts(project.id).then(setReceipts).catch(() => setReceipts({ receipts: [] }));
    }
  }, [section, project.id]);

  const prepare = async (body) => {
    setBusy(true);
    try {
      const res = await opsApi.poPrepare(project.id, body);
      toast.success(`Prepared ${res.prepared.join(", ")} — review before it goes out`);
      await load();
      setSection("pos");
      setOpenId(res.purchase_orders[0]?.id || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Nothing could be prepared"); }
    finally { setBusy(false); }
  };
  const openPo = (id) => { setSection("pos"); setOpenId(id); };
  const setStage = async (po, stage) => {
    try { await opsApi.poStage(project.id, po.id, { stage }); toast.success(`${po.po_number} → ${stage}`); await load(); reload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change the stage"); }
  };

  if (!data) {
    return <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading purchasing…
    </div>;
  }
  const s = data.summary;
  const openPoRow = data.purchase_orders.find((p) => p.id === openId);
  const poIdx = data.purchase_orders.findIndex((p) => p.id === openId);
  if (section === "pos" && openId && focus && openPoRow) {
    const po = openPoRow;
    return (
      <div className="space-y-4" data-testid="ops-po-focus">
        <div className="flex items-center gap-2 text-sm">
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setOpenId("")}
                  data-testid="ops-po-focus-back">
            <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Purchasing
          </Button>
          <span className="text-slate-300">/</span>
          <span className="font-mono text-xs text-slate-600">{po.po_number}</span>
          <span className="ml-2 flex items-center gap-1">
            <button type="button" data-kb-prev disabled={poIdx <= 0} onClick={() => setOpenId(data.purchase_orders[poIdx - 1].id)}
                    data-testid="ops-po-focus-prev" title="Previous PO (←)"
                    className="rounded-md border border-slate-200 p-1 text-slate-500 hover:bg-slate-50 disabled:opacity-30">
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button type="button" data-kb-next disabled={poIdx >= data.purchase_orders.length - 1} onClick={() => setOpenId(data.purchase_orders[poIdx + 1].id)}
                    data-testid="ops-po-focus-next" title="Next PO (→)"
                    className="rounded-md border border-slate-200 p-1 text-slate-500 hover:bg-slate-50 disabled:opacity-30">
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
            <span className="text-[11px] text-slate-400">{poIdx + 1} / {data.purchase_orders.length}</span>
          </span>
          <button type="button" onClick={toggleFocus} data-testid="ops-po-collapse"
                  title="Collapse back into the purchase order list"
                  className="ml-auto rounded-md border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-800">
            <Minimize2 className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-3" data-testid="ops-po-focus-header">
          <span className="font-mono text-base font-semibold text-slate-900">{po.po_number}</span>
          <span className="text-sm text-slate-700">{po.manufacturer}</span>
          <span className="font-mono text-xs text-slate-500">
            {(po.cost_codes || []).join(", ") || "no cost code"}{po.scope_codes?.length ? ` · ${po.scope_codes.join(" · ")}` : ""}
          </span>
          <StagePill po={po} canEdit={data.can_edit} onChange={(stage) => setStage(po, stage)} />
          <span className="ml-auto text-base font-semibold tabular-nums text-slate-900">{money(po.total)}</span>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white">
          <PoDetail project={project} poId={po.id} canEdit={data.can_edit} canIssue={data.can_issue}
                    onChanged={() => { load(); reload?.(); }} />
        </div>
        {quoteFor !== false && (
          <BoardQuoteDialog project={project} group={quoteFor} onClose={() => setQuoteFor(false)}
                            onApplied={() => { setQuoteFor(false); load(); reload?.(); }} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ops-purchasing-tab">
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="ops-po-pills">
        <Pill label="PO's" tid="ops-po-pill-pos" title="Ordered purchase orders / not yet ordered (prepared POs + queue groups)">
          {n(s.pos_ordered)}<span className="text-slate-400"> / </span>{n(s.pos_not_ordered)}
        </Pill>
        <Pill label="Ordered" tid="ops-po-pill-ordered" title="Units not yet ordered / FRS demand">
          {n(s.not_ordered)}<span className="text-slate-400"> / </span>{n(s.demand)}
        </Pill>
        <Pill label="On hand" tid="ops-po-pill-on-hand" title="Units physically checked in (warehouse or on site) / not on hand">
          {n(s.on_hand)}<span className="text-slate-400"> / </span>{n(s.not_on_hand)}
        </Pill>
        <Pill label="Surplus" tid="ops-po-pill-surplus" title="Delivered beyond the FRS — from the field on-hand ledger">
          {n(s.surplus)}
        </Pill>
        <Pill label="Ready" tid="ops-po-pill-ready" title="Queue groups whose submittals are approved">
          {n(s.ready)}<span className="text-slate-400"> / {n(s.groups)}</span>
        </Pill>
        <Pill label="Late" tid="ops-po-pill-late" title="Red: lead time runs past the scope start · Yellow: within 10 days of it">
          {n(s.late_red + s.late_yellow)}
          {(s.late_red > 0 || s.late_yellow > 0) && (
            <span className="ml-1 text-[11px] font-normal text-slate-500">
              {s.late_red > 0 && `${s.late_red} red`}{s.late_red > 0 && s.late_yellow > 0 && " · "}{s.late_yellow > 0 && `${s.late_yellow} yellow`}
            </span>
          )}
        </Pill>
      </div>

      {!data.project_open && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800"
             data-testid="ops-po-not-open">
          This project is <b>{STATUS_WORD[data.project_status] || "not Open"}</b>. A purchase order can still be
          prepared; issuing one will ask for a written authorization or draft the request to open
          the job.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => { setAllOpen((v) => !v); if (allOpen) setOpenId(""); }}
                data-testid="ops-po-expand-all" title={allOpen ? "Collapse all" : "Expand all"}
                className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-800">
          {allOpen ? <ChevronsDownUp className="h-3.5 w-3.5" /> : <ChevronsUpDown className="h-3.5 w-3.5" />}
        </button>
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {SECTIONS.map(([key, label]) => (
            <button key={key} onClick={() => setSection(key)}
                    data-testid={`ops-po-section-${key}`}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      section === key ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"}`}>
              {label}
            </button>
          ))}
        </div>
        <Button variant="data" size="sm" className="text-xs" data-testid="ops-po-export"
                onClick={() => opsApi.poLogExport(project.id).catch(() => toast.error("The Excel log could not be downloaded"))}>
          <Share className="mr-1.5 h-3.5 w-3.5" /> Excel
        </Button>
        <Button variant="data" size="sm" className="text-xs" data-testid="ops-po-export-pdf"
                onClick={() => opsApi.poLogPdf(project.id).catch(() => toast.error("The PDF log could not be downloaded"))}>
          <Share className="mr-1.5 h-3.5 w-3.5" /> PDF
        </Button>
        {data.can_edit && (
          <Button variant="data" size="sm" className="text-xs" onClick={() => setQuoteFor(null)}
                  title="Load vendor quotes against the queue" data-testid="ops-po-download-quotes">
            <Download className="mr-1.5 h-3.5 w-3.5" /> Quotes
          </Button>
        )}
        <Button variant="data" size="sm" className="text-xs" onClick={emailLog} data-testid="ops-po-email-log"
                title="Downloads the procurement log PDF and opens a draft in your own email app">
          <Mail className="mr-1.5 h-3.5 w-3.5" /> Log
        </Button>
      </div>

      {section === "board" && (
        <>
          <ReadinessBoard data={data} busy={busy} onPrepare={prepare} project={project} onChanged={load}
                          onQuoteFor={setQuoteFor} onOpenPo={openPo} expandAll={allOpen} />
        </>
      )}

      {section === "pos" && (
        <div className="space-y-3" data-testid="ops-po-list">
          {data.purchase_orders.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-600">
              <ShoppingCart className="mx-auto mb-2 h-5 w-5 text-slate-400" />
              No purchase orders yet. Prepare one from the queue — it is built from the
              FRS, the approved submittal and the change orders, at vendor cost.
            </div>
          ) : null}
          {data.purchase_orders.map((po) => {
            const isOpen = allOpen || openId === po.id;
            return (
              <div key={po.id} className="rounded-xl border border-slate-200 bg-white">
                <button onClick={() => { if (allOpen) { setAllOpen(false); setOpenId(po.id); } else setOpenId(openId === po.id ? "" : po.id); }}
                        data-testid={`ops-po-row-${po.po_number}`} aria-expanded={isOpen}
                        className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left">
                  {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
                  <span className="font-mono text-sm font-semibold text-slate-900">
                    {po.po_number}
                  </span>
                  <span className="text-sm text-slate-700">{po.manufacturer}</span>
                  <span className="font-mono text-xs text-slate-500">
                    {(po.cost_codes || []).join(", ") || "no cost code"}{po.scope_codes?.length ? ` · ${po.scope_codes.join(" · ")}` : ""}
                  </span>
                  <StagePill po={po} canEdit={data.can_edit} onChange={(stage) => setStage(po, stage)} />
                  <span className="ml-auto text-sm font-semibold tabular-nums text-slate-900">
                    {money(po.total)}
                  </span>
                  {isOpen && (
                    <span role="button" onClick={(e) => { e.stopPropagation(); setAllOpen(false); setOpenId(po.id); setFocus(true); localStorage.setItem("ops.po.focus", "1"); }}
                          data-testid={`ops-po-focus-open-${po.po_number}`} title="Open full view"
                          className="rounded-md border border-slate-200 p-1 text-slate-500 hover:bg-slate-50 hover:text-slate-800">
                      <Maximize2 className="h-3.5 w-3.5" />
                    </span>
                  )}
                </button>
                {isOpen && (
                  <PoDetail project={project} poId={po.id} canEdit={data.can_edit}
                            canIssue={data.can_issue}
                            onChanged={() => { load(); reload?.(); }} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {section === "receiving" && (
        <div className="rounded-xl border border-slate-200 bg-white" data-testid="ops-po-receiving">
          {!receipts ? (
            <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading receipts…
            </div>
          ) : receipts.receipts.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-600">
              Nothing received yet. Receiving is recorded on the purchase order and shows up in the
              field material counts — there is no second inventory.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-1.5 text-left">Received</th>
                  <th className="px-3 py-1.5 text-left">PO</th>
                  <th className="px-3 py-1.5 text-left">Part</th>
                  <th className="px-3 py-1.5 text-left">Description</th>
                  <th className="px-3 py-1.5 text-right">Qty</th>
                  <th className="px-3 py-1.5 text-right">Value</th>
                  <th className="px-3 py-1.5 text-left">By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {receipts.receipts.map((r) => (
                  <tr key={r.id} data-testid={`ops-po-receipt-${r.id}`}>
                    <td className="px-3 py-1.5">{(r.received_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{r.po_number}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{r.item_number || "—"}</td>
                    <td className="px-3 py-1.5">{r.description}</td>
                    <td className="px-3 py-1.5 text-right">{Number(r.qty).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right">{money(r.amount)}</td>
                    <td className="px-3 py-1.5 text-xs text-slate-500">{r.received_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {section === "inbox" && (
        <InboxSection project={project} purchaseOrders={data.purchase_orders} canEdit={data.can_edit}
                      onChanged={() => { load(); reload?.(); }} onOpenPo={openPo} />
      )}

      <p className="flex items-start gap-2 text-xs text-slate-400">
        <Layers className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Purchase orders are built from the Current FRS, the approved submittal and the Process
        Approved / Unapproved change order lines, priced at vendor cost. Sell price never appears
        on a purchase order, and our cost codes stay off the vendor's copy.
      </p>
      {quoteFor !== false && (
        <BoardQuoteDialog project={project} group={quoteFor} onClose={() => setQuoteFor(false)}
                          onApplied={() => { setQuoteFor(false); load(); reload?.(); }} />
      )}
    </div>
  );
}

const STAGES = ["Draft", "Submitted", "Shipped", "On hand"];
const STAGE_TONE = { Draft: "bg-slate-100 text-slate-700", Submitted: "bg-indigo-100 text-indigo-700",
  Shipped: "bg-teal-100 text-teal-800", "On hand": "bg-emerald-100 text-emerald-700", Void: "bg-rose-100 text-rose-700" };

/** One pill: the PO's stage — set automatically by email / acknowledgement / ship notice /
 *  receipt, overridable by hand from the pill itself. */
function StagePill({ po, canEdit, onChange }) {
  const [open, setOpen] = useState(false);
  const stage = po.status === "void" ? "Void" : po.stage;
  return (
    <span className="relative" onClick={(e) => e.stopPropagation()}>
      <button type="button" disabled={!canEdit || po.status === "void"} onClick={() => setOpen((v) => !v)}
              data-testid={`ops-po-stage-${po.po_number}`}
              title={po.status === "void" ? "Void" : [po.status_label,
                po.shipped_on ? `shipped ${po.shipped_on}` : "",
                po.received_at ? `received ${po.received_at.slice(0, 10)}` : "",
                po.billing_flag ? "flagged for billing" : "", po.billed_on ? `billed on ${po.billed_on}` : ""].filter(Boolean).join(" · ") + " — click to change"}
              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STAGE_TONE[stage] || "bg-slate-100"} ${canEdit ? "hover:ring-2 hover:ring-slate-300" : ""}`}>
        {stage}
      </button>
      {open && (
        <div className="absolute left-0 top-6 z-20 w-36 rounded-md border border-slate-200 bg-white p-1 shadow-lg"
             data-testid={`ops-po-stage-menu-${po.po_number}`}>
          {STAGES.map((st) => (
            <button key={st} type="button" onClick={() => { setOpen(false); if (st !== stage) onChange(st); }}
                    data-testid={`ops-po-stage-option-${po.po_number}-${st.replace(" ", "-")}`}
                    className={`block w-full rounded px-2 py-1 text-left text-xs hover:bg-slate-100 ${st === stage ? "font-semibold" : ""}`}>
              {st}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function Pill({ label, tid, title, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={tid}>
      <p className="text-xs text-slate-500"><Hint text={title}>{label}</Hint></p>
      <p className="text-lg font-semibold tabular-nums text-slate-900">{children}</p>
    </div>
  );
}
